import React, {
  createContext,
  useState,
  useMemo,
  useEffect,
  useContext,
} from "react";

const goldTheme = {
  colors: {
    primary: "#bf9a2f",
    background: "#ffffff",
    text: "#000000",
    border: "#cccccc",
    notification: "#ff453a",
  },
  className: "theme-gold",
};

export const THEME_MODE = {
  SYSTEM: "system",
  LIGHT: "light",
  DARK: "dark",
};

const ThemeContext = createContext({
  themeName: "Gold",
  appearance: "light",
  theme: goldTheme,
  themeMode: THEME_MODE.SYSTEM,
  toggleTheme: () => {},
  setThemeMode: () => {},
});

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }) => {
  const prefersDarkMode =
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches;

  const [themeMode, setThemeModeState] = useState(() => {
    return localStorage.getItem("themeMode") || THEME_MODE.SYSTEM;
  });

  const [appearance, setAppearance] = useState(() => {
    const savedMode = localStorage.getItem("themeMode") || THEME_MODE.SYSTEM;
    if (savedMode === THEME_MODE.SYSTEM) {
      return prefersDarkMode ? "dark" : "light";
    }
    return savedMode === THEME_MODE.DARK ? "dark" : "light";
  });

  // Apply theme classes to document element
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("dark");
    root.classList.add(goldTheme.className);

    if (appearance === "dark") {
      root.classList.add("dark");
    }
  }, [appearance]);

  // Listen for system theme changes
  useEffect(() => {
    if (themeMode === THEME_MODE.SYSTEM) {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      const handleChange = (e) => {
        setAppearance(e.matches ? "dark" : "light");
      };

      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }
  }, [themeMode]);

  // Set theme mode (system, light, dark) and save preference
  const setThemeMode = (mode) => {
    setThemeModeState(mode);
    localStorage.setItem("themeMode", mode);

    let newAppearance;
    if (mode === THEME_MODE.SYSTEM) {
      newAppearance = prefersDarkMode ? "dark" : "light";
    } else {
      newAppearance = mode === THEME_MODE.DARK ? "dark" : "light";
    }
    setAppearance(newAppearance);
  };

  // Toggle between light and dark (overrides system)
  const toggleTheme = () => {
    const newAppearance = appearance === "dark" ? "light" : "dark";
    setAppearance(newAppearance);
    const newMode =
      newAppearance === "dark" ? THEME_MODE.DARK : THEME_MODE.LIGHT;
    setThemeModeState(newMode);
    localStorage.setItem("themeMode", newMode);
  };

  const theme = useMemo(
    () => ({
      ...goldTheme,
      dark: appearance === "dark",
      colors: {
        ...goldTheme.colors,
        background: appearance === "dark" ? "#000000" : goldTheme.colors.background,
        text: appearance === "dark" ? "#ffffff" : goldTheme.colors.text,
        border: appearance === "dark" ? "#333333" : goldTheme.colors.border,
        card: appearance === "dark" ? "#1E1E1E" : "#FFFFFF",
        error: "#ff453a",
      },
    }),
    [appearance]
  );

  return (
    <ThemeContext.Provider
      value={{
        themeName: "Gold",
        appearance,
        theme,
        themeMode,
        toggleTheme,
        setThemeMode,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;